import { Checkbox, Divider, Drawer, Slider, Space, Tooltip, Table, InputNumber } from "antd";
import React, { useState, useEffect } from "react";
import EditorPanelHeading from "../../UI/EditorPanelHeading";
import { getColor } from "../../../utils/ConvertCSS";
import { useTemplate } from "../../../utils/Template";
import ColourPicker from "../../../globals/styles/ColourPicker";
import PresetPicker from "../../../globals/styles/PresetPicker";
import { PaddingHorizontalIcon, PaddingVerticalIcon, CheckIcon } from "../../Icons";
import BMInput from "../../comps/BMInput";
import Tables from "../../UI/Tables";
import { makeKey, tt_name_block_warning, tt_padding_x, tt_padding_y } from "../../../utils/Utils";
import MobileSelector from "../../UI/MobileSelector";
import SliderInput from "../../UI/SiderInput";
import Html from "../../../utils/Html";
import { PicRightOutlined, PicLeftOutlined, PicCenterOutlined } from '@ant-design/icons';

export default function TableGrid(props) {

    const { edit = false, close = () => { }, saveItem = () => { }, deleteItem = () => { }, id = 0, view = "desktop", setView = () => { } } = props;
    const [content, setContent] = useState(props.content);
    const [template,] = useTemplate();
    const [mobile, setMobile] = useState(false);
    const [dataRow, setDataRow] = useState([]);

    const setData = (_data) => {
        setContent({ ...content, "data": _data })
    }

    // Component agnostic
    const {
        name = "",
        container = true,
        data = {},
        date_changed = 0
    } = content;

    // Component specific
    const {
        background_colour = 0,
        header_bg_colour = 0,
        row_preset = 0,
        row_colour = 0,
        alternating_colour = 0,
        fixed_width = false,
        border_thickness = 0,
        border_colour = 0,
        has_width = false,
        has_height = false,
        alternating = false,
        css_id = "",
        height = 1000,
        width = 1248,
        align = "left",
        padding_x = 10,
        padding_y = 10,
        columns = [
            { header: "Column 1", padding_x: 0, padding_y: 0, text_align: "left", header_preset: 0, min_width: 200 },
            { header: "Column 2", padding_x: 0, padding_y: 0, text_align: "left", header_preset: 0, min_width: 200 },
            { header: "Column 3", padding_x: 0, padding_y: 0, text_align: "left", header_preset: 0, min_width: 200 },
        ],
        rows = [
            { data: "Row 1 Column 1", check: false }, { data: "Row 1 Column 2", check: false }, { data: "Row 1 Column 3", check: false },
        ],
        allow_mobile_overrides = false,
        mobile_width = 100,
        mobile_height = 200,

    } = data;

    const save = () => {
        if (!name) {
            let _name = prompt(tt_name_block_warning);
            if (_name) {
                close();
                saveItem(id, { ...content, "name": _name });
            } else {
                return;
            }
        } else {
            close();
            saveItem(id, { ...content })
        }
    }

    const cssId = (e) => {
        setData({ ...data, "css_id": e })
    }

    useEffect(() => {
        cssId(id);
    }, [id]);

    const uniqueId = `table-${css_id}`;

    const dataRows = () => {
        const newRows = rows.map(row => row);
        const groupedRows = [];

        for (let i = 0; i < newRows.length; i += columns.length) {
            const group = newRows.slice(i, i + columns.length);
            const existingId = dataRow[Math.floor(i / columns.length)]?.id;
            groupedRows.push({ ...group, id: existingId || makeKey() });
        }

        setDataRow(groupedRows);
    };

    useEffect(dataRows, [columns, rows])

    const dataColumns = columns.map((col, i) => ({
        title:
            <div style={{ textAlign: col.text_align, padding: col.padding_y + "px " + col.padding_x + "px", minWidth: fixed_width ? col.min_width + "px" : '', maxWidth: fixed_width ? col.min_width + "px" : '' }}>
                <div className={"bm-component bm-txt-" + col.header_preset}>
                    <Html html={col.header} />
                </div>
            </div>,
        dataIndex: i,
        key: i,
        render: (e = {}, f, rowIndex) => {
            return {
                props: {
                    style: { background: alternating ? rowIndex % 2 === 1 ? getColor(alternating_colour, template) : getColor(row_colour, template) : row_colour === 0 ? "" : getColor(row_colour, template) }
                },
                children:
                    <div style={{ textAlign: col.text_align, padding: col.padding_y + "px " + col.padding_x + "px", minWidth: fixed_width ? col.min_width + "px" : '', maxWidth: fixed_width ? col.min_width + "px" : '' }}>
                        <div className={"bm-component bm-txt-" + row_preset} >
                            {e.check ? <CheckIcon /> : ""}
                            <Html html={e.data} />
                        </div>
                    </div>
            };
        }
    }));

    const trash = () => {
        deleteItem(id);
        close();
    }

    const drawRender = () => {

        let _align = {
            background: getColor(background_colour, template),
            padding: `${padding_x}px ${padding_y}px`,
            maxWidth: "100%",
            display: fixed_width || has_width ? "flex" : "",
            flexDirection: fixed_width || has_width ? "column" : "",
        }

        let _table = {
            justifyContent: "space-evenly",
            height: (allow_mobile_overrides && view !== "desktop") ? mobile_height + "px" : (has_height) ? height + "px" : "auto",
            maxWidth: (allow_mobile_overrides && view !== "desktop") ? mobile_width + "%" : (has_width) ? width + "px" : "100%",
            overflowY: "auto",
            width: has_width ? "100%" : "",
        }

        switch (align) {
            case "center":
                _table.alignSelf = "center";
                break;
            case "right":
                _table.alignSelf = "flex-end";
                break;
            default:
                _table.alignSelf = "flex-start";
                break;
        }

        return (
            <div id={"bm-" + id} className={(container) ? "preview-container" : ""}>
                <style>
                    {`
                        .${uniqueId} .ant-table-thead .ant-table-cell {
                            background-color: ${getColor(header_bg_colour, template)};
                        }

                        .${uniqueId} .ant-table table {
                            border-collapse: collapse;
                        }
                            
                        .${uniqueId} 
                        .ant-table-tbody > tr > td,
                        .ant-table-thead > tr > th
                        {
                            border-top-right-radius: 0 !important;
                            border-top-left-radius: 0 !important;
                        }
                    
                        .${uniqueId} .ant-table-thead > tr > th:not(:last-child):not(.ant-table-selection-column):not(.ant-table-row-expand-icon-cell):not([colspan])::before {
                            display: none;
                        }

                        .${uniqueId} .ant-table-tbody > tr > td {
                            border: ${border_thickness}px solid ${getColor(border_colour, template)};
                        }

                        .${uniqueId} .ant-table-thead > tr > th {
                            border: ${border_thickness}px solid ${getColor(border_colour, template)};
                            border-bottom: none;

                        }
                    `}
                </style>
                <div style={_align}>
                    <div style={_table}>
                        <Table
                            size="small"
                            className={uniqueId}
                            dataSource={dataRow}
                            columns={dataColumns}
                            pagination={false}
                            scroll={{
                                x: fixed_width ? "max-content" : "",
                            }}
                        />
                    </div>
                </div>

            </div>
        )
    }

    const drawForm = () => {
        return (
            <>
                <EditorPanelHeading menus={props.menus} close={close} trash={trash} save={save} content={content} setContent={setContent} date_changed={date_changed} name={name} />
                <MobileSelector setView={setView} onChange={(e) => setMobile(e)} />
                <div className="shim" /><div className="shim" />
                {(mobile && drawMobile())}
                {(!mobile && drawDesktop())}
            </>
        )
    }

    const drawMobile = () => {
        return (
            <>
                <div>
                    <Checkbox onChange={(e) => setData({ ...data, "allow_mobile_overrides": e.target.checked })} checked={allow_mobile_overrides}><small>Enable Overides?</small></Checkbox>
                    <div className="shim" /><div className="shim" />

                    {/* WIDTH */}
                    <Divider orientationMargin={0} orientation="left" plain className="simple-divider">MAX WIDTH</Divider>
                    <div className="float-flex">
                        <Slider
                            width={0}
                            style={{ width: "150px" }}
                            min={0}
                            max={100}
                            tooltip={{
                                formatter: (value) => `${value}%`,
                            }}
                            onChange={(_v) => setData({ ...data, "mobile_width": _v })}
                            disabled={!allow_mobile_overrides}
                            value={mobile_width}
                        />
                        <InputNumber
                            className="simple-num-input"
                            min={0}
                            max={100}
                            style={{ margin: '0 16px', "width": "40px" }}
                            value={mobile_width}
                            onBlur={(_v) => setData({ ...data, "mobile_width": _v })}
                            controls={false}
                            precision={0}
                        />
                    </div>

                    {/* HEIGHT */}
                    <Divider orientationMargin={0} orientation="left" plain className="simple-divider">MAX HEIGHT</Divider>
                    <SliderInput
                        width={150}
                        min={0}
                        max={500}
                        onChange={(_v) => setData({ ...data, "mobile_height": _v })}
                        disabled={!allow_mobile_overrides}
                        value={mobile_height}
                    />
                </div>
            </>
        )
    }

    const drawDesktop = () => {
        return (
            <>
                <Divider orientationMargin={0} orientation="left" plain className="simple-divider">BACKGROUND COLOUR</Divider>
                <ColourPicker allow_gradient allow_none value={background_colour} onChange={(_v) => setData({ ...data, "background_colour": _v })} />
                <div className="shim" /> <div className="shim" />

                <Divider orientationMargin={0} orientation="left" plain className="simple-divider">HEADER BACKGROUND COLOUR</Divider>
                <ColourPicker allow_gradient allow_none value={header_bg_colour} onChange={(_v) => setData({ ...data, "header_bg_colour": _v })} />
                <div className="shim" /> <div className="shim" />

                {/* MAX WIDTH */}
                <Checkbox
                    checked={fixed_width}
                    onChange={(e) => setData({ ...data, "fixed_width": e.target.checked })}>
                    <small>Fixed Column Width</small>
                </Checkbox>
                <div className="shim" /><div className="shim" />
                <Divider orientationMargin={0} orientation="left" plain className="simple-divider">MAX WIDTH</Divider>
                <div className="float-flex">
                    <SliderInput
                        width={90}
                        min={0}
                        max={1248}
                        onChange={(_v) => setData({ ...data, "width": _v })}
                        disabled={!has_width}
                        value={width}
                    />
                    <Checkbox onChange={(e) => setData({ ...data, "has_width": e.target.checked })} checked={has_width}><small>Constrain?</small></Checkbox>
                </div>

                {/* MAX HEIGHT */}
                <Divider orientationMargin={0} orientation="left" plain className="simple-divider">MAX HEIGHT</Divider>
                <div className="float-flex">
                    <SliderInput
                        width={90}
                        min={0}
                        max={1000}
                        onChange={(_v) => setData({ ...data, "height": _v })}
                        disabled={!has_height}
                        value={height}
                    />
                    <Checkbox onChange={(e) => setData({ ...data, "has_height": e.target.checked })} checked={has_height}><small>Constrain?</small></Checkbox>
                </div>

                {/* ALIGN */}
                <Divider orientationMargin={0} orientation="left" plain className="simple-divider">ALIGN</Divider>
                <div className="float-flex">
                    <Space>
                        <div><PicLeftOutlined onClick={() => setData({ ...data, "align": "left" })} style={{ "color": (align === "left") ? "#1890ff" : "#AAA" }} /></div>
                        <div><PicCenterOutlined onClick={() => setData({ ...data, "align": "center" })} style={{ "color": (align === "center") ? "#1890ff" : "#AAA" }} /></div>
                        <div><PicRightOutlined onClick={() => setData({ ...data, "align": "right" })} style={{ "color": (align === "right") ? "#1890ff" : "#AAA" }} /></div>
                    </Space>
                </div>
                <div className="shim" /> <div className="shim" />

                <Tables container={container} extra data={data} setData={setData} columns={columns} rows={rows} dataRow={dataRow} setDataRow={setDataRow} />
                <div className="shim" /> <div className="shim" />

                {/* PADDING */}
                <Divider orientationMargin={0} orientation="left" plain className="simple-divider">PADDING</Divider>
                <div className="float-flex">
                    <Space>
                        <BMInput width={35} value={padding_y} onChange={(_v) => setData({ ...data, "padding_y": _v })} number min={0} max={100} prefix={<Tooltip title={tt_padding_y}><PaddingHorizontalIcon style={{ "color": "#888" }} /></Tooltip>} />
                        <BMInput width={35} value={padding_x} onChange={(_v) => setData({ ...data, "padding_x": _v })} number min={0} max={100} prefix={<Tooltip title={tt_padding_x}><PaddingVerticalIcon style={{ "color": "#888" }} /></Tooltip>} />
                    </Space>
                </div>
                <div className="shim" /> <div className="shim" />

                {/* COLOUR */}
                <Divider orientationMargin={0} orientation="left" plain className="simple-divider">ROW TEXT PRESET</Divider>
                <PresetPicker value={row_preset} onChange={(e) => setData({ ...data, "row_preset": e })} />
                <div className="shim" /> <div className="shim" />
                <Divider orientationMargin={0} orientation="left" plain className="simple-divider">ROW BACKGROUND COLOUR</Divider>
                <ColourPicker allow_gradient allow_none value={row_colour} onChange={(_v) => setData({ ...data, "row_colour": _v })} />
                <div className="shim" /> <div className="shim" />
                <div style={{ display: alternating ? "block" : "none" }} >
                    <Divider orientationMargin={0} orientation="left" plain className="simple-divider">ROW BACKGROUND SECOND COLOUR</Divider>
                    <ColourPicker allow_gradient allow_none value={alternating_colour} onChange={(_v) => setData({ ...data, "alternating_colour": _v })} />
                    <div className="shim" /> <div className="shim" />
                </div>

                {/* BUTTONS */}
                <Checkbox
                    checked={alternating}
                    onChange={(e) => setData({ ...data, "alternating": e.target.checked })}>
                    <small>Alternating Row</small>
                </Checkbox>
                <div className="shim" /> <div className="shim" />

                {/* BORDER */}
                <Divider orientationMargin={0} orientation="left" plain className="simple-divider">BORDER THICKNESS</Divider>
                <SliderInput
                    width={150}
                    min={0}
                    max={10}
                    onChange={(_v) => setData({ ...data, "border_thickness": _v })}
                    value={border_thickness}
                />
                <Divider orientationMargin={0} orientation="left" plain className="simple-divider">BORDER COLOR</Divider>
                <ColourPicker allow_gradient allow_none value={border_colour} onChange={(_v) => setData({ ...data, "border_colour": _v })} />
            </>
        )
    }

    return (
        <>
            <Drawer bodyStyle={{ "backgroundColor": "#f5f5f5", "overflowX": "hidden" }} closable={false} width={300} onClose={() => close(true)} open={edit}>
                {drawForm()}
            </Drawer>
            {drawRender()}
        </>
    )

}